import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import API_CONFIG from '../../config/api.config';
import {
  DragIndicator as DragIcon,
  CheckCircle as EnabledIcon,
  RadioButtonUnchecked as DisabledIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';

const CATEGORY_LABELS = {
  Header: 'Header',
  'Order Info': 'Order Details',
  'Items Table': 'Items Table',
  Summary: 'Summary',
  Footer: 'Footer'
};

const fieldCategories = {
  Header: [
    { field: 'logo', label: 'Logo', description: 'Company logo' },
    { field: 'companyName', label: 'Company Name', description: 'Company name' },
    { field: 'companyAddress', label: 'Company Address', description: 'Company address' },
    { field: 'contact', label: 'Contact', description: 'Contact number' },
    { field: 'email', label: 'Email', description: 'Email address' },
    { field: 'uen', label: 'GST', description: 'GST number' },
    { field: 'bankDetails', label: 'Bank Details', description: 'Bank information' }
  ],
  'Order Info': [
    { field: 'orderNumber', label: 'Order Number', description: 'Order number' },
    { field: 'orderDate', label: 'Order Date', description: 'Order date' },
    { field: 'customerName', label: 'Customer Name', description: 'Customer name' },
    { field: 'customerAddress', label: 'Customer Address', description: 'Customer address' },
    { field: 'customerContact', label: 'Customer Contact', description: 'Customer contact' },
    { field: 'paymentMethod', label: 'Payment Method', description: 'Payment method' },
    { field: 'paymentStatus', label: 'Payment Status', description: 'Payment status' },
    { field: 'previousDueAmount', label: 'Previous Due Amount', description: 'Previous due amount' },
    { field: 'salesman', label: 'Salesman Name', description: 'Salesman / cashier name' }
  ],
  'Items Table': [
    { field: 'sno', label: 'S.No', description: 'Serial number' },
    { field: 'productName', label: 'Product Name', description: 'Product name' },
    { field: 'mrp', label: 'MRP', description: 'Maximum retail price' },
    { field: 'hsn', label: 'HSN', description: 'HSN code' },
    { field: 'uom', label: 'UOM', description: 'Unit of measurement' },
    { field: 'sellingPrice', label: 'Selling Price', description: 'Selling price' },
    { field: 'quantity', label: 'Qty', description: 'Quantity' },
    { field: 'discount', label: 'Discount', description: 'Discount amount' },
    { field: 'subtotal', label: 'Subtotal', description: 'Subtotal' },
    { field: 'taxRate', label: 'Tax %', description: 'Tax rate percentage' },
    { field: 'taxAmount', label: 'Tax Amount', description: 'Tax amount' },
    { field: 'cgst', label: 'CGST', description: 'Central GST' },
    { field: 'sgst', label: 'SGST', description: 'State GST' },
    { field: 'total', label: 'Total', description: 'Total amount' },
    { field: 'expiryDate', label: 'Expiry Date', description: 'Product expiry date' },
    { field: 'batchNumber', label: 'Batch Number', description: 'Product batch number' }
  ],
  Summary: [
    { field: 'subtotal', label: 'Subtotal', description: 'Subtotal amount' },
    { field: 'discount', label: 'Discount', description: 'Discount amount' },
    { field: 'taxAmount', label: 'Tax Amount', description: 'Total tax amount' },
    { field: 'cgst', label: 'CGST', description: 'Central GST' },
    { field: 'sgst', label: 'SGST', description: 'State GST' },
    { field: 'shippingCost', label: 'Shipping Cost', description: 'Shipping cost' },
    { field: 'total', label: 'Total', description: 'Total amount' },
    { field: 'totalSaved', label: 'Total Saved', description: 'MRP minus selling price × qty (below total)' },
    { field: 'amountInWords', label: 'Amount in Words', description: 'Total in words' }
  ],
  Footer: [
    { field: 'thankYouMessage', label: 'Thank You Message', description: 'Thank you message' },
    { field: 'companyName', label: 'Company Name', description: 'Company name' },
    { field: 'footerImage', label: 'Footer Image (QR Code)', description: 'QR code for shop location or reviews' },
    { field: 'authorizedSignature', label: 'Authorized Signature', description: 'Authorized signature' },
    { field: 'bankDetails', label: 'Bank Details', description: 'Bank information' },
    { field: 'terms', label: 'Terms & Conditions', description: 'Terms and conditions' }
  ]
};

const SECTION_ORDER = ['Header', 'Order Info', 'Items Table', 'Summary', 'Footer'];

/**
 * FieldPalette — accordion sections so users expand only what they need (less scrolling).
 */
const FieldPalette = ({
  fields,
  onFieldClick,
  onColumnReorder,
  companySettings,
  onFooterImageUpload,
  onCompanySettingsUpdate
}) => {
  const [expandedSection, setExpandedSection] = useState('Header');
  const footerImageInputRef = useRef(null);
  const footerUploadBoxRef = useRef(null);

  const footerImageEnabled = fields?.footer?.footerImage?.enabled;

  useEffect(() => {
    if (footerImageEnabled) {
      setExpandedSection('Footer');
      if (footerUploadBoxRef.current) {
        footerUploadBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [footerImageEnabled]);

  const handleDragStart = (e, field) => {
    e.dataTransfer.setData('application/json', JSON.stringify(field));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const isFieldEnabled = (fieldName, category) => {
    if (!fields) return false;

    if (category === 'Items Table') {
      const columns = fields.itemsTable?.columns || [];
      const column = columns.find((col) => col.field === fieldName);
      return column?.enabled || false;
    }
    if (category === 'Header') {
      return fields.header?.[fieldName]?.enabled || false;
    }
    if (category === 'Order Info') {
      return fields.orderInfo?.[fieldName]?.enabled || false;
    }
    if (category === 'Summary') {
      return fields.summary?.[fieldName]?.enabled || false;
    }
    if (category === 'Footer') {
      return fields.footer?.[fieldName]?.enabled || false;
    }
    return false;
  };

  const countEnabledInSection = (category, categoryFields) => {
    if (category === 'Items Table') {
      const columns = fields?.itemsTable?.columns || [];
      return columns.filter((col) => col.enabled).length;
    }
    return categoryFields.filter((f) => isFieldEnabled(f.field, category)).length;
  };

  const renderFieldRow = (field, category) => {
    const enabled = isFieldEnabled(field.field, category);
    return (
      <ListItem
        key={field.field}
        draggable
        onDragStart={(e) => handleDragStart(e, { ...field, category })}
        onClick={() => onFieldClick && onFieldClick({ ...field, category })}
        sx={{
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          '&:hover': { backgroundColor: 'action.hover' },
          border: '1px solid',
          borderColor: enabled ? 'success.main' : 'divider',
          borderRadius: 1,
          mb: 0.5,
          bgcolor: enabled ? 'success.light' : 'background.paper'
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <DragIcon color="action" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight="medium">
                {field.label}
              </Typography>
              {enabled && (
                <Chip label="On" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {field.description}
            </Typography>
          }
        />
        <ListItemIcon sx={{ minWidth: 36 }}>
          {enabled ? (
            <EnabledIcon color="success" fontSize="small" />
          ) : (
            <DisabledIcon color="disabled" fontSize="small" />
          )}
        </ListItemIcon>
      </ListItem>
    );
  };

  const renderItemsTableSection = (categoryFields) => {
    const columns = fields?.itemsTable?.columns || [];
    const enabledColumns = columns.filter((col) => col.enabled);

    return (
      <>
        {enabledColumns.length > 0 && (
          <Box sx={{ mb: 1, p: 1, bgcolor: 'action.selected', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Column order (use arrows to reorder):
            </Typography>
            <List dense disablePadding>
              {enabledColumns.map((col, index) => {
                const fieldDef = categoryFields.find((f) => f.field === col.field);
                return (
                  <ListItem
                    key={`enabled-${col.field}`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'success.main',
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: 'success.light',
                      py: 0.5
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <DragIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {col.label || fieldDef?.label || col.field}
                          </Typography>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            color="primary"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        disabled={index === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onColumnReorder && index > 0) {
                            const newColumns = [...columns];
                            const colIndex = columns.findIndex((c) => c.field === col.field);
                            const prevColIndex = columns.findIndex(
                              (c) => c.field === enabledColumns[index - 1].field
                            );
                            [newColumns[colIndex], newColumns[prevColIndex]] = [
                              newColumns[prevColIndex],
                              newColumns[colIndex]
                            ];
                            onColumnReorder(newColumns);
                          }
                        }}
                        sx={{ minWidth: 32, p: 0.5 }}
                      >
                        <ArrowUpIcon fontSize="small" />
                      </Button>
                      <Button
                        size="small"
                        disabled={index === enabledColumns.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onColumnReorder && index < enabledColumns.length - 1) {
                            const newColumns = [...columns];
                            const colIndex = columns.findIndex((c) => c.field === col.field);
                            const nextColIndex = columns.findIndex(
                              (c) => c.field === enabledColumns[index + 1].field
                            );
                            [newColumns[colIndex], newColumns[nextColIndex]] = [
                              newColumns[nextColIndex],
                              newColumns[colIndex]
                            ];
                            onColumnReorder(newColumns);
                          }
                        }}
                        sx={{ minWidth: 32, p: 0.5 }}
                      >
                        <ArrowDownIcon fontSize="small" />
                      </Button>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
        <List dense disablePadding>
          {categoryFields.map((field) => renderFieldRow(field, 'Items Table'))}
        </List>
      </>
    );
  };

  const renderFooterExtras = () => {
    if (!isFieldEnabled('footerImage', 'Footer')) return null;
    return (
      <Box
        ref={footerUploadBoxRef}
        sx={{
          mt: 1,
          p: 1.5,
          bgcolor: 'primary.light',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        <Typography variant="caption" fontWeight="medium" color="primary.dark" sx={{ display: 'block', mb: 1 }}>
          Upload QR code or image for bill footer
        </Typography>
        {companySettings?.footerImageUrl ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <img
              src={(() => {
                const url = companySettings.footerImageUrl;
                if (!url) return '';
                if (url.startsWith('http')) return url;
                const apiOrigin =
                  (API_CONFIG.BASE_URL || '').replace(/\/api\/?$/, '') || window.location.origin;
                return url.startsWith('/') ? `${apiOrigin}${url}` : url;
              })()}
              alt="Footer QR"
              style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4 }}
            />
            <Typography variant="caption">Image uploaded</Typography>
            <Button size="small" variant="outlined" onClick={() => footerImageInputRef.current?.click()}>
              Change
            </Button>
          </Box>
        ) : (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              footerImageInputRef.current?.click();
            }}
          >
            Upload Image
          </Button>
        )}
        <input
          ref={footerImageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file && onFooterImageUpload) {
              try {
                const result = await onFooterImageUpload(file);
                if (result?.footerImageUrl && onCompanySettingsUpdate) {
                  onCompanySettingsUpdate({
                    ...(companySettings || {}),
                    footerImageUrl: result.footerImageUrl
                  });
                }
              } catch (err) {
                console.error('Footer image upload failed:', err);
              }
              e.target.value = '';
            }
          }}
        />
      </Box>
    );
  };

  return (
    <Paper
      elevation={2}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="h6" component="h2">
          Bill Sections
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Tap a section to expand · click fields to turn on/off
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
          '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' }
        }}
      >
        {SECTION_ORDER.map((category) => {
          const categoryFields = fieldCategories[category];
          const enabledCount = countEnabledInSection(category, categoryFields);
          const label = CATEGORY_LABELS[category] || category;

          return (
            <Accordion
              key={category}
              disableGutters
              elevation={0}
              expanded={expandedSection === category}
              onChange={(_, isExpanded) => setExpandedSection(isExpanded ? category : false)}
              sx={{
                '&:before': { display: 'none' },
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: expandedSection === category ? 'action.hover' : 'background.paper'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1 }
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                  {label}
                </Typography>
                <Chip
                  label={`${enabledCount} on`}
                  size="small"
                  color={enabledCount > 0 ? 'success' : 'default'}
                  variant={enabledCount > 0 ? 'filled' : 'outlined'}
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1 }}>
                {category === 'Items Table'
                  ? renderItemsTableSection(categoryFields)
                  : (
                    <List dense disablePadding>
                      {categoryFields.map((field) => renderFieldRow(field, category))}
                    </List>
                  )}
                {category === 'Footer' && renderFooterExtras()}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Paper>
  );
};

export default FieldPalette;
